import React, { useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { logger } from "../lib/logger";
import { toIntlLocale } from "@core/lib/date-utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@core/components/ui/tooltip";
import { useDragToSelect } from "@core/hooks/useDragToSelect";
import { useIsMobile } from "@core/hooks/use-mobile";
import { useWeekStart } from "@core/hooks/useWeekStart";

interface CalendarProps {
  visibleMonths: Date[];
  selectedDates: string[];
  onDateToggle: (date: Date) => void;
  onBatchDateToggle?: (dates: Date[], action: "add" | "remove") => void;
  onMonthChange: (direction: "prev" | "next") => void;
  onMonthsChange?: (months: Date[]) => void;
  editMode?: boolean;
  /** Cadence récurrente : ajoute un anneau violet sur les jours sélectionnés. */
  recurringMark?: boolean;
  /** Regroupement week-end confirmé (bouton "Réunir les week-ends" cliqué) —
   * n'affiche le connecteur visuel samedi+dimanche que si vrai, pour ne pas
   * donner l'impression que le vote est déjà groupé avant confirmation. */
  weekendGroupingEnabled?: boolean;
}

interface CalendarDay {
  date: Date | null;
  isCurrentMonth: boolean;
  isEmpty: boolean;
}

const Calendar = React.forwardRef<HTMLDivElement, CalendarProps>(
  (
    {
      visibleMonths,
      selectedDates,
      onDateToggle,
      onBatchDateToggle,
      onMonthChange,
      onMonthsChange,
      editMode = false,
      recurringMark = false,
      weekendGroupingEnabled = false,
    },
    ref,
  ) => {
    // Timer optimisé pour éviter les conflits
    const timerId = logger.time("Calendar - Rendu total", "calendar");
    logger.log(
      `Calendar - Début du rendu (${visibleMonths.length} mois, ${selectedDates.length} dates sélectionnées)`,
      "calendar",
    );

    const { t, i18n } = useTranslation("common");
    const intlLocale = toIntlLocale(i18n.language);
    // Premier jour de la semaine : 0 = dimanche, 1 = lundi (préf + locale).
    const { weekStart } = useWeekStart();
    // Jours abrégés localisés. Base : 2024-01-01 = lundi, 2024-01-07 = dimanche.
    const weekDays = useMemo(() => {
      const base =
        weekStart === 0 ? new Date(2024, 0, 7) : new Date(2024, 0, 1);
      return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(base);
        d.setDate(base.getDate() + i);
        const s = d.toLocaleDateString(intlLocale, { weekday: "short" });
        return s.charAt(0).toUpperCase() + s.slice(1).replace(/\.$/, "");
      });
    }, [intlLocale, weekStart]);
    const calendarRef = useRef<HTMLDivElement>(null);

    // Helper: Formater une date en string YYYY-MM-DD
    const formatDateStr = (date: Date): string => {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    // Helper: Obtenir toutes les dates entre deux dates
    const getDatesInRange = (start: Date, end: Date): Date[] => {
      const [earlierDate, laterDate] =
        start <= end ? [start, end] : [end, start];
      const dates: Date[] = [];
      const currentDate = new Date(earlierDate);

      while (currentDate <= laterDate) {
        dates.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }

      return dates;
    };

    // Drag-to-select avec le hook réutilisable
    const {
      isDragging,
      handleDragStart,
      handleDragMove,
      handleDragEnd,
      isDraggedOver,
      isLongPressActive,
    } = useDragToSelect<Date>({
      // Clic simple sur une date (sans drag)
      onClickItem: (date) => {
        const dateStr = formatDateStr(date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const isPastDay = date < today;

        logger.info("Calendar - day clicked", "calendar", {
          date: dateStr,
          isPastDay,
        });

        if (!isPastDay || editMode) {
          onDateToggle(date);
        }
      },
      // Drag de sélection de plage
      onDragEnd: (draggedItems, startDate) => {
        if (!startDate || draggedItems.size === 0) return;

        const startDateStr = formatDateStr(startDate);
        const wasSelected = selectedDates.includes(startDateStr);

        // Si onBatchDateToggle est disponible, l'utiliser pour un seul appel
        if (onBatchDateToggle) {
          const datesToToggle: Date[] = [];
          draggedItems.forEach((dateStr) => {
            const isCurrentlySelected = selectedDates.includes(dateStr);
            if (
              (wasSelected && isCurrentlySelected) ||
              (!wasSelected && !isCurrentlySelected)
            ) {
              datesToToggle.push(new Date(dateStr));
            }
          });
          if (datesToToggle.length > 0) {
            onBatchDateToggle(datesToToggle, wasSelected ? "remove" : "add");
          }
        } else {
          // Fallback: appeler onDateToggle pour chaque date (comportement original)
          draggedItems.forEach((dateStr) => {
            const date = new Date(dateStr);
            const isCurrentlySelected = selectedDates.includes(dateStr);

            if (wasSelected && isCurrentlySelected) {
              onDateToggle(date);
            } else if (!wasSelected && !isCurrentlySelected) {
              onDateToggle(date);
            }
          });
        }
      },
      getItemKey: formatDateStr,
      getItemsInRange: getDatesInRange,
      canDragItem: (date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date >= today || editMode; // Drag les dates passées en mode édition
      },
      disableOnMobile: true,
    });

    // Mise en cache des jours du calendrier avec useMemo
    const generateCalendarDays = useMemo(() => {
      const memoTimerId = logger.time(
        "Calendar - useMemo generateCalendarDays",
        "calendar",
      );

      const result = (monthDate: Date): CalendarDay[] => {
        const dayTimerId = logger.time(
          "Calendar - Génération des jours d'un mois",
          "calendar",
        );
        // Générer seulement les jours du mois en cours
        const year = monthDate.getFullYear();
        const month = monthDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        // Position du 1er du mois dans la grille selon le premier jour de
        // semaine choisi (weekStart : 0=Dim, 1=Lun). getDay() : 0=Dim..6=Sam.
        const firstDayOfWeek = (firstDay.getDay() - weekStart + 7) % 7;

        const days = [];

        // Espaces vides au début pour aligner correctement le premier jour
        for (let i = 0; i < firstDayOfWeek; i++) {
          days.push({ date: null, isCurrentMonth: false, isEmpty: true });
        }

        // Jours du mois en cours seulement
        for (let i = 1; i <= lastDay.getDate(); i++) {
          const currentDate = new Date(year, month, i);
          days.push({
            date: currentDate,
            isCurrentMonth: true,
            isEmpty: false,
          });
        }

        logger.timeEnd(dayTimerId);
        return days;
      };
      logger.timeEnd(memoTimerId);
      return result;
    }, [weekStart]); // Recalcule le padding si le 1er jour de semaine change

    // Détecter si on est sur mobile
    const isMobile = () => {
      return window.innerWidth < 768; // md breakpoint
    };

    const renderCalendarGrid = (month: Date, isMobile: boolean = false) => {
      const days = generateCalendarDays(month);

      return (
        <div className="space-y-2">
          {/* En-têtes des jours */}
          <div className="grid grid-cols-7 gap-1 px-2 py-2 bg-background rounded-lg">
            {weekDays.map((day) => (
              <div
                key={day}
                className={`text-center text-muted-foreground py-1 font-medium ${
                  isMobile ? "text-sm" : "text-sm"
                }`}
              >
                {day}
              </div>
            ))}
          </div>

          {/* Grille du calendrier */}
          <div
            className="grid grid-cols-7 gap-1 place-items-center"
            style={isMobile ? { touchAction: "none" } : undefined}
          >
            {days.map(({ date, isCurrentMonth, isEmpty }, index) => {
              // Placement grid explicite pour chaque jour : nécessaire pour
              // coexister avec le connecteur week-end (lui aussi placé
              // explicitement plus bas) sans que l'algorithme d'auto-placement
              // CSS Grid ne "réserve" sa cellule avant les jours et ne décale
              // tout le reste — cf. commentaire au niveau du connecteur.
              const gridRow = Math.floor(index / 7) + 1;
              const gridColumn = (index % 7) + 1;

              // Si c'est une case vide, on affiche juste un espace
              if (isEmpty || !date) {
                return (
                  <div
                    key={`empty-${index}`}
                    className="w-11 h-11"
                    style={{ gridRow, gridColumn }}
                  />
                );
              }

              const year = date.getFullYear();
              const monthNum = (date.getMonth() + 1)
                .toString()
                .padStart(2, "0");
              const dayStr = date.getDate().toString().padStart(2, "0");
              const dateStr = `${year}-${monthNum}-${dayStr}`;
              const isSelected = selectedDates.includes(dateStr);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const isToday = date.getTime() === today.getTime();
              const isPastDay = date < today;

              const isDateDraggedOver = isDraggedOver(dateStr);

              // Pour les dates passées, afficher un span au lieu d'un button
              // SAUF en mode édition où on les rend cliquables
              if (isPastDay && !editMode) {
                return (
                  <div
                    key={`${dateStr}-past`}
                    data-date={dateStr}
                    aria-disabled="true"
                    className={`w-11 h-11 text-sm rounded-lg transition-all duration-200 font-medium flex items-center justify-center
                    text-muted-foreground bg-muted cursor-not-allowed`}
                    style={{ gridRow, gridColumn }}
                  >
                    {date.getDate()}
                  </div>
                );
              }

              return (
                <motion.button
                  key={`${dateStr}-${isSelected ? "selected" : "unselected"}-${isDateDraggedOver ? "dragged" : ""}`}
                  type="button"
                  aria-label={date.toLocaleDateString(intlLocale, {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                  aria-pressed={isSelected}
                  initial={{
                    scale: 1,
                    boxShadow: "0 0 0 0 hsl(var(--primary) / 0)",
                  }}
                  animate={
                    isSelected
                      ? {
                          scale: [1, 1.15, 1, 1.05, 1, 1.05, 1],
                          boxShadow: [
                            "0 0 0 0 hsl(var(--primary) / 0)",
                            "0 0 0 4px hsl(var(--primary) / 0.4)",
                            "0 0 0 8px hsl(var(--primary) / 0)",
                            "0 0 0 4px hsl(var(--primary) / 0.4)",
                            "0 0 0 8px hsl(var(--primary) / 0)",
                            "0 0 0 4px hsl(var(--primary) / 0.4)",
                            "0 0 0 0 hsl(var(--primary) / 0)",
                          ],
                        }
                      : {
                          scale: 1,
                          boxShadow: "0 0 0 0 hsl(var(--primary) / 0)",
                        }
                  }
                  transition={{
                    duration: 2,
                    ease: "easeInOut",
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  // Drag-to-select + clic unifiés via useDragToSelect
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    handleDragStart(date, e);
                  }}
                  onPointerMove={(e) => {
                    if (isDragging) {
                      handleDragMove(date, e);
                    }
                  }}
                  onPointerUp={() => {
                    handleDragEnd();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onDateToggle(date);
                    }
                  }}
                  data-date={dateStr}
                  data-viewport={isMobile ? "mobile" : "desktop"}
                  className={`relative z-10 w-11 h-11 text-sm rounded-lg transition-all duration-200 font-medium flex items-center justify-center touch-none
                  ${
                    isDateDraggedOver && isDragging
                      ? "bg-primary/50 text-primary-foreground border-2 border-primary"
                      : isSelected
                        ? `bg-primary text-primary-foreground shadow-md hover:bg-primary/90${
                            recurringMark
                              ? " ring-2 ring-inset ring-purple-400" // color-exempt: recurrence marker, distinct from primary
                              : isToday
                                ? " ring-2 ring-offset-2 ring-offset-background ring-primary/70"
                                : ""
                          }`
                        : isLongPressActive && isDateDraggedOver
                          ? "animate-pulse ring-2 ring-primary"
                          : isToday
                            ? "text-primary hover:bg-accent after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1 after:h-1 after:rounded-full after:bg-primary"
                            : "text-foreground hover:bg-accent"
                  }`}
                  style={{
                    gridRow,
                    gridColumn,
                    ...(isMobile ? { touchAction: "none" } : undefined),
                  }}
                >
                  {date.getDate()}
                </motion.button>
              );
            })}

            {/* Connecteur visuel week-end : samedi+dimanche sélectionnés ensemble
                rendus comme un seul bloc. Placement explicite en grid-column,
                rendu après les boutons (auto-placés) donc sans effet sur leur
                position — cf. https://drafts.csswg.org/css-grid/#auto-placement-algo.
                Uniquement quand la semaine démarre le lundi (weekStart=1) :
                samedi/dimanche sont alors toujours adjacents sur la même ligne
                (colonnes 6-7). Avec weekStart=0 (en-US), dimanche ouvre la
                semaine suivante sur une autre ligne — pas de connecteur.
                Conditionné à weekendGroupingEnabled : sans ça, le calendrier
                montrait un bloc fusionné avant même que l'utilisateur ait
                confirmé le regroupement via le bouton "Réunir les week-ends",
                laissant croire que le vote était déjà groupé. */}
            {weekendGroupingEnabled &&
              weekStart === 1 &&
              days.map(({ date, isEmpty }, index) => {
                if (isEmpty || !date || date.getDay() !== 6) return null;
                const nextDay = days[index + 1];
                if (!nextDay || nextDay.isEmpty || !nextDay.date) return null;
                if (nextDay.date.getDay() !== 0) return null;

                const dateStr = formatDateStr(date);
                const nextDateStr = formatDateStr(nextDay.date);
                if (
                  !selectedDates.includes(dateStr) ||
                  !selectedDates.includes(nextDateStr)
                ) {
                  return null;
                }

                const row = Math.floor(index / 7) + 1;
                const col = (index % 7) + 1;

                return (
                  <div
                    key={`weekend-connector-${dateStr}`}
                    aria-hidden="true"
                    className="bg-primary rounded-lg pointer-events-none"
                    style={{
                      gridColumn: `${col} / ${col + 2}`,
                      gridRow: row,
                      justifySelf: "stretch",
                      alignSelf: "center",
                      width: "100%",
                      height: "2.75rem",
                      zIndex: 0,
                    }}
                  />
                );
              })}
          </div>
        </div>
      );
    };

    // Gestion du scroll avec lazy loading pour desktop
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollLeft, scrollWidth, clientWidth } = e.currentTarget;

      // Charger plus de mois si on approche de la fin (jusqu'à 60 mois = 5 ans)
      if (
        scrollWidth - scrollLeft <= clientWidth + 200 &&
        visibleMonths.length < 60
      ) {
        const lastMonth = visibleMonths[visibleMonths.length - 1];

        // Charger 3 mois à la fois pour navigation plus fluide
        const newMonths: Date[] = [];
        for (let i = 1; i <= 3; i++) {
          const nextMonth = new Date(
            lastMonth.getFullYear(),
            lastMonth.getMonth() + i,
            1,
          );

          // Vérifier qu'on ne dépasse pas 5 ans depuis AUJOURD'HUI (dynamique)
          // Ex: En 2025 → jusqu'en 2030, En 2028 → jusqu'en 2033, etc.
          const fiveYearsFromNow = new Date();
          fiveYearsFromNow.setFullYear(fiveYearsFromNow.getFullYear() + 5);

          if (nextMonth <= fiveYearsFromNow) {
            newMonths.push(nextMonth);
          }
        }

        if (newMonths.length > 0 && onMonthsChange) {
          onMonthsChange([...visibleMonths, ...newMonths]);
        }
      }
    };

    const handleDesktopMonthNav = (direction: "prev" | "next") => {
      onMonthChange(direction);

      const container = calendarRef.current;
      if (!container) return;

      const step = container.clientWidth / 2; // environ un mois si deux sont visibles

      if (direction === "prev") {
        container.scrollLeft = Math.max(0, container.scrollLeft - step);
      } else {
        const maxScroll = container.scrollWidth - container.clientWidth;
        container.scrollLeft = Math.min(maxScroll, container.scrollLeft + step);
      }
    };

    // Terminer le timer principal avant le rendu
    logger.timeEnd(timerId);

    const isMobileViewport = useIsMobile();

    return (
      <div
        ref={ref}
        className="w-full"
        data-testid="calendar"
        style={{ userSelect: isDragging ? "none" : "auto" }}
      >
        {/* Mobile: Un seul mois avec navigation */}
        {isMobileViewport && (
          <div>
            {visibleMonths.length > 0 && (
              <div className="border border-border rounded-lg p-3 bg-card overflow-hidden">
                {/* Navigation mobile */}
                <TooltipProvider>
                  <div className="flex items-center justify-between mb-3">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => onMonthChange("prev")}
                          className="p-2 rounded-lg hover:bg-accent transition-colors"
                          aria-label={t("month_previous")}
                        >
                          <ChevronLeft className="w-5 h-5 text-foreground" />
                        </motion.button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t("month_previous")}</p>
                      </TooltipContent>
                    </Tooltip>

                    <AnimatePresence mode="wait">
                      <motion.h3
                        key={visibleMonths[0]?.getTime()}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="text-lg font-semibold text-foreground"
                      >
                        {visibleMonths[0]?.toLocaleDateString(intlLocale, {
                          month: "long",
                          year: "numeric",
                        })}
                      </motion.h3>
                    </AnimatePresence>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => onMonthChange("next")}
                          className="p-2 rounded-lg hover:bg-accent transition-colors"
                          aria-label={t("month_next")}
                        >
                          <ChevronRight className="w-5 h-5 text-foreground" />
                        </motion.button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t("month_next")}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </TooltipProvider>

                {/* Calendrier mobile sans swipe horizontal - navigation via les boutons seulement */}
                <div className="relative">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={visibleMonths[0]?.getTime()}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{
                        duration: 0.3,
                        ease: "easeInOut",
                      }}
                      className="select-none"
                    >
                      {renderCalendarGrid(visibleMonths[0], true)}
                    </motion.div>
                  </AnimatePresence>

                  {/* Indicateurs visuels de swipe */}
                  <div className="absolute top-1/2 left-2 transform -translate-y-1/2 opacity-30 pointer-events-none">
                    <ChevronLeft className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="absolute top-1/2 right-2 transform -translate-y-1/2 opacity-30 pointer-events-none">
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {!isMobileViewport && (
          <TooltipProvider>
            <div className="relative w-full mx-auto px-12">
              {/* Flèche gauche - dans le gutter externe, hors zone scrollable */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => handleDesktopMonthNav("prev")}
                    className="absolute left-0 top-6 z-20 bg-card/90 backdrop-blur-sm hover:bg-accent shadow-lg rounded-full p-2 transition-all border border-border hover:shadow-xl"
                    aria-label={t("month_previous")}
                    data-testid="calendar-back"
                  >
                    <ChevronLeft className="w-5 h-5 text-foreground" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("month_previous")}</p>
                </TooltipContent>
              </Tooltip>

              {/* Flèche droite - dans le gutter externe, hors zone scrollable */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => handleDesktopMonthNav("next")}
                    className="absolute right-0 top-6 z-20 bg-card/90 backdrop-blur-sm hover:bg-accent shadow-lg rounded-full p-2 transition-all border border-border hover:shadow-xl"
                    aria-label={t("month_next")}
                    data-testid="calendar-next"
                  >
                    <ChevronRight className="w-5 h-5 text-foreground" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("month_next")}</p>
                </TooltipContent>
              </Tooltip>

              <div
                ref={calendarRef}
                onScroll={handleScroll}
                className="overflow-x-auto border border-border rounded-lg bg-card relative"
                style={{
                  scrollSnapType: "x mandatory",
                  scrollbarWidth: "thin",
                  WebkitOverflowScrolling: "touch",
                }}
              >
                <div className="flex gap-4 min-w-max px-4 py-3">
                  {visibleMonths.map((month, index) => (
                    <motion.div
                      key={`${month.getFullYear()}-${month.getMonth()}`}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex-none w-72 sm:w-80 xl:w-96"
                      style={{ scrollSnapAlign: "start" }}
                    >
                      {/* Titre du mois */}
                      <div className="mb-3 text-center">
                        <h3 className="text-base sm:text-lg font-semibold text-foreground">
                          {month.toLocaleDateString(intlLocale, {
                            month: "long",
                            year: "numeric",
                          })}
                        </h3>
                      </div>

                      {renderCalendarGrid(month, false)}
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </TooltipProvider>
        )}
      </div>
    );
  },
);

Calendar.displayName = "Calendar";

export default Calendar;
